import React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "../config/Constants";
import DatePicker, { Calendar, DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/red.css";
import dayjs from "dayjs";
import DatePanel from "react-multi-date-picker/plugins/date_panel";

interface Props {
  placeholder: string;
  label?: string;
  onDateSelect: (event: any) => void;
  value?: any;
  name?: string;
  required: boolean;
  helperText: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

interface StyledInputProps {
  helperText: string;
  fullWidth: boolean;
}

const MultiDateSelector = ({
  placeholder,
  label,
  value,
  name,
  required,
  helperText,
  disabled,
  onDateSelect,
  fullWidth,
}: Props) => {
  const [dropdown, setShowDropdown] = useState(false);

  const useOutsideAlerter = (ref: any) => {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowDropdown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };
  const wrapperRef = useRef(null);

  useOutsideAlerter(wrapperRef);
  const format = "YYYY/DD/DD";

  const [dates, setDates] = useState([]);

  const handleDateChange = (dates: any) => {
    // setDates(dates);
    onDateSelect({ target: { value: dates, name: name } });
  };

  useEffect(() => {
    let dates = value.map((val: any) => {
      let splitDate = val.split("/");
      return new DateObject().set({
        day: splitDate[2],
        month: splitDate[1],
        year: splitDate[0],
        format: format,
      });
    });
    setDates(dates);
  }, [value]);

  return (
    <>
      <InputFieldWrapper ref={wrapperRef}>
        {label && (
          <Label>
            {label}
            {required && <span>*</span>}
          </Label>
        )}
        <InputWrapper helperText={helperText}>
          <InputField
            disabled={disabled}
            placeholder={placeholder}
            value={`${dates?.length} Date${
              dates?.length > 1 ? "s" : ""
            } selected`}
            name={name}
            helperText={helperText}
            autoComplete="off"
            onClick={() => setShowDropdown(true)}
            // @ts-ignore
            fullWidth={fullWidth}
          />
          <CalendarIcon
            src={require("../assets/images/datepicker-icon.png")}
            alt="clear"
            onClick={() => setShowDropdown(true)}
          />
        </InputWrapper>
        {helperText && <Error>{helperText}</Error>}
        {dropdown && (
          <DropdownList>
            <FieldWrapper>
              <StyledCalendar
                className="red"
                value={dates}
                onChange={handleDateChange}
                multiple
                sort
                format={format}
                minDate={new Date()}
              />
            </FieldWrapper>
          </DropdownList>
        )}
      </InputFieldWrapper>
    </>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div<{ helperText: string }>`
  position: relative;
  border: 1px solid
    ${(props) => (props.helperText ? Colors.red : Colors.athensGrey)};
  border-radius: 0.25rem;
`;

const InputFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  // margin-bottom: 1rem;
  width: 100%;
`;

const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: absolute;
  top: 4.5rem;
  width: fit-content;
  background: white;
  border: 1px solid ${Colors.athensGrey};
  border-radius: 0.25rem;
  // max-height: 15rem;
  overflow-y: auto;
  padding: 1rem;
  box-sizing: border-box;
  z-index: 99;
  @media only screen and (max-width: 768px) {
    max-width: 100%;
  }
`;

const InputField = styled.input<StyledInputProps>`
  width: ${(props) => (props.fullWidth ? "95%" : "12rem")};
  align-items: center;
  font-family: "Poppins-Medium";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 12px;
  padding: 0.75rem 0.5rem;
  border-radius: 0.25rem;
  border: none;
  ::placeholder {
    color: ${Colors.autumnFog};
  }
  :focus {
    outline: none;
  }
  :disabled {
    background: ${Colors.white};
    color: ${Colors.cloudyGrey};
    cursor: no-drop;
  }
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;
const Label = styled.label`
  font-family: "Poppins-Medium";
  font-style: normal;
  font-weight: 350;
  font-size: 13px;
  line-height: 16px;
  color: ${Colors.electricBlue};
  margin-bottom: 0.5rem;
  > span {
    color: ${Colors.red};
    margin-left: 2px;
  }
`;

const Error = styled.span`
  color: ${Colors.red};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const CalendarIcon = styled.img`
  cursor: pointer;
  width: 1.25rem;
  height: 1.25rem;
  position: absolute;
  background: white;
  position: absolute;
  right: 0.5rem;
  top: 0.75rem;
  @media only screen and (max-width: 768px) {
    right: -0.75rem;
  }
`;

const StyledCalendar = styled(Calendar)`
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: none;

  .day {
    border-radius: 5px;
    transition: background-color 0.3s;
    border: 1px solid black;
    &:hover {
      background-color: #f0f0f0;
    }
  }

  .selected {
    background-color: ${Colors.red};
    color: #fff;

    &:hover {
      background-color: #375a8c;
    }
  }

  .week-days {
    font-weight: bold;
    color: #555;
  }

  .month-nav {
    background-color: #4e73df;
    color: #fff;
  }
`;

export default MultiDateSelector;
